import { useState, useEffect, useRef } from 'react';
import { useGameState } from '../../../net/GameProvider.js';
import { useI18n } from '../../../i18n.js';
import { CardUI } from '../../CardUI.js';
import { Icon } from '../../Icon.js';
import { HandType, rankOf } from '@big2/shared';
import { getPlayerPosition, type PlayerPosition } from '../common/TablePosition.js';

type SpecialComboType = 'fullHouse' | 'fourOfAKind' | 'straightFlush' | 'royalFlush';

const COMBO_PALETTES: Record<SpecialComboType, string[]> = {
  royalFlush: ['#fbbf24', '#f43f5e', '#a855f7', '#38bdf8', '#34d399', '#fef08a'],
  straightFlush: ['#f43f5e', '#06b6d4', '#fbbf24', '#a855f7', '#fde047'],
  fourOfAKind: ['#f59e0b', '#ef4444', '#fbbf24', '#f97316', '#ffedd5'],
  fullHouse: ['#34d399', '#10b981', '#fbbf24', '#059669', '#fef08a'],
};

/**
 * Hardware-accelerated radial particle explosion on HTML5 Canvas for rare Big Two combos.
 */
function ComboParticleBurst({ type }: { type: SpecialComboType }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let width = 0;
    let height = 0;

    const updateSize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    const colors = COMBO_PALETTES[type] ?? COMBO_PALETTES.fullHouse;
    const particleCount = type === 'royalFlush' ? 120 : type === 'straightFlush' ? 100 : 80;
    const originX = width / 2;
    const originY = height * 0.42;

    interface BurstParticle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
      rotation: number;
      rotationSpeed: number;
      oscillation: number;
      oscillationSpeed: number;
      opacity: number;
      isSpark: boolean;
    }

    const particles: BurstParticle[] = Array.from({ length: particleCount }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 8 + 3.2;
      return {
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - (Math.random() * 3.5 + 1),
        size: Math.random() * 7 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.25,
        oscillation: Math.random() * Math.PI * 2,
        oscillationSpeed: Math.random() * 0.12 + 0.04,
        opacity: 1,
        isSpark: Math.random() > 0.6,
      };
    });

    const startTime = performance.now();
    const duration = 2400;

    const render = (now: number) => {
      const elapsed = now - startTime;
      ctx.clearRect(0, 0, width, height);

      let aliveCount = 0;

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.96;
        p.vy += 0.16; // gravity
        p.rotation += p.rotationSpeed;
        p.oscillation += p.oscillationSpeed;

        const progress = Math.min(1, elapsed / duration);
        p.opacity = Math.max(0, 1 - progress * 1.15);

        if (p.opacity > 0) {
          aliveCount++;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.globalAlpha = p.opacity;

          if (p.isSpark) {
            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = p.color;
            ctx.shadowBlur = 8;
            ctx.beginPath();
            ctx.arc(0, 0, p.size * 0.35, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillStyle = p.color;
            const w = p.size * Math.cos(p.oscillation);
            const h = p.size;
            ctx.fillRect(-w / 2, -h / 2, w, h);
          }
          ctx.restore();
        }
      }

      if (aliveCount > 0 && elapsed < duration) {
        animId = requestAnimationFrame(render);
      }
    };

    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', updateSize);
    };
  }, [type]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 65,
      }}
    />
  );
}

interface TrickHistoryItem {
  id: string;
  handType?: HandType;
  cards: string[];
  playerId: string;
  name: string;
}

export function Big2Table({ isPaused }: { isPaused: boolean }) {
  const { state } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const { game } = state;

  const [clearingTrick, setClearingTrick] = useState<{ cards: string[]; name: string; winnerPosition: PlayerPosition } | null>(null);
  const [trickHistory, setTrickHistory] = useState<TrickHistoryItem[]>([]);
  const [specialComboAnim, setSpecialComboAnim] = useState<{ id: string; type: SpecialComboType; name: string } | null>(null);
  const lastSpecialComboRef = useRef<string | null>(null);

  useEffect(() => {
    if (!game?.trick) {
      if (trickHistory.length > 0 && !clearingTrick) {
        const lastPlay = trickHistory[trickHistory.length - 1];
        const winnerPosition = getPlayerPosition(lastPlay.playerId, game?.myPlayerId ?? '', game?.players ?? []);
        setClearingTrick({
          cards: lastPlay.cards,
          name: lastPlay.name,
          winnerPosition,
        });
        setTimeout(() => {
          setClearingTrick(null);
          setTrickHistory([]);
        }, 900);
      }
      return;
    }

    if (game.trick) {
      const trickCardsStr = game.trick.cards.join(',');
      const isNewCombo = lastSpecialComboRef.current !== trickCardsStr;

      if (isNewCombo) {
        lastSpecialComboRef.current = trickCardsStr;
        const handType = game.trick.handType;
        if (handType === 'fullHouse' || handType === 'fourOfAKind' || handType === 'straightFlush') {
          let isRoyal = false;
          if (handType === 'straightFlush') {
            const ranks = new Set(game.trick.cards.map((c) => rankOf(c)));
            if (ranks.has('A') && ranks.has('K') && (ranks.has('Q') || ranks.has('T'))) {
              isRoyal = true;
            }
          }
          const animType: SpecialComboType = isRoyal ? 'royalFlush' : (handType as SpecialComboType);
          const playerName = game.players.find((p) => p.playerId === game.trick!.playerId)?.name || 'Someone';
          setSpecialComboAnim({ id: Date.now().toString(), type: animType, name: playerName });
          setTimeout(() => setSpecialComboAnim(null), 2500);
        }
      }

      setTrickHistory((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.playerId === game.trick!.playerId && last.cards.join(',') === game.trick!.cards.join(',')) {
          return prev;
        }
        return [
          ...prev,
          {
            id: `${game.trick!.playerId}-${game.trick!.cards.join('-')}-${Date.now()}`,
            handType: game.trick!.handType,
            cards: game.trick!.cards,
            playerId: game.trick!.playerId,
            name: game.players.find((p) => p.playerId === game.trick!.playerId)?.name || 'Unknown',
          },
        ];
      });
    }
  }, [game?.trick, game?.players, trickHistory, clearingTrick]);

  if (!game) return null;

  return (
    <>
      {/* Special Combo Animation Overlay */}
      {specialComboAnim && (
        <>
          <ComboParticleBurst type={specialComboAnim.type} />
          <div key={specialComboAnim.id} className="special-combo-anim-container">
            <div className={`special-combo-anim-text is-${specialComboAnim.type}`}>
              {specialComboAnim.type === 'royalFlush'
                ? isMn
                  ? '👑 РОЯАЛ СТРЕЙТ ФЛАШ!'
                  : '👑 ROYAL STRAIGHT FLUSH!'
                : specialComboAnim.type === 'straightFlush'
                ? isMn
                  ? '⚡ СТРЕЙТ ФЛАШ!'
                  : '⚡ STRAIGHT FLUSH!'
                : specialComboAnim.type === 'fourOfAKind'
                ? isMn
                  ? '💥 ДӨРВӨН ИЖИЛ (КАРЕ)!'
                  : '💥 FOUR OF A KIND!'
                : isMn
                ? '🔥 ФУЛЛ ХАУС!'
                : '🔥 FULL HOUSE!'}
            </div>
          </div>
        </>
      )}

      <div className={`game-table-center${game.players.length === 2 || game.players.length === 4 ? ' has-top-opponent' : ''}`}>
        {/* Permanent table center watermark */}
        <img
          src="/card-table-center.svg"
          className="table-center-watermark"
          alt=""
          aria-hidden="true"
        />

        {clearingTrick ? (
          <div className={`trick-clearing-container sweep-to-${clearingTrick.winnerPosition}`}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {clearingTrick.cards.map((c, i) => (
                <CardUI
                  key={`clearing-${c}-${i}`}
                  faceDown={isPaused}
                  cardCode={isPaused ? undefined : c}
                  className={i > 0 ? 'trick-card trick-card-overlap' : 'trick-card'}
                  style={{
                    transform: `rotate(${(i - (clearingTrick.cards.length - 1) / 2) * 3.5}deg)`,
                    boxShadow: '0 4px 14px rgba(214, 196, 138, 0.45)',
                  }}
                />
              ))}
            </div>
            <div className="trick-clearing-badge">
              <Icon name="trophy" size={14} />
              <span>{isMn ? `${clearingTrick.name} модоо авлаа!` : `${clearingTrick.name} won the trick!`}</span>
            </div>
          </div>
        ) : trickHistory.length > 0 ? (
          <div className="trick-history-stack">
            {(() => {
              const visibleTricks = trickHistory.slice(-3);
              return visibleTricks.map((th, idx) => {
                const isLast = idx === visibleTricks.length - 1;
                const offsetFromLatest = idx - visibleTricks.length + 1; // 0 for latest, -1 for previous, -2 for 2nd previous
                const offsetX = offsetFromLatest * 14;
                const offsetY = offsetFromLatest * 14;
                const rotOffset = offsetFromLatest * -2.4;
                const origin = getPlayerPosition(th.playerId, game.myPlayerId, game.players);

                return (
                  <div
                    key={th.id}
                    className={`trick-play-layer ${isLast ? 'is-latest' : 'is-stacked'}`}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      zIndex: isLast ? 10 : idx + 2,
                      transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) rotate(${rotOffset}deg) scale(${
                        isLast ? 1 : idx === visibleTricks.length - 2 ? 0.94 : 0.88
                      })`,
                      opacity: isLast ? 1 : idx === visibleTricks.length - 2 ? 0.72 : 0.44,
                      filter: isLast
                        ? 'drop-shadow(0 14px 24px rgba(0, 0, 0, 0.45))'
                        : 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.35))',
                      transition:
                        'transform 300ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease-out, filter 300ms ease-out',
                    }}
                  >
                    <div
                      className={`trick-card-fan ${isLast ? `trick-play-snap trick-play-from-${origin}` : ''}`}
                      style={{ display: 'flex', justifyContent: 'center' }}
                    >
                      {th.cards.map((c, i) => (
                        <CardUI
                          key={`${c}-${i}`}
                          faceDown={isPaused}
                          cardCode={isPaused ? undefined : c}
                          className={i > 0 ? 'trick-card trick-card-overlap' : 'trick-card'}
                          style={{
                            transform: `rotate(${(i - (th.cards.length - 1) / 2) * 3.6}deg)`,
                          }}
                        />
                      ))}
                    </div>
                    {isLast && !isPaused && (
                      <div className="trick-label">
                        {t.trickFrom(t.handLabels[th.handType as HandType], th.name)}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        ) : null}
      </div>
    </>
  );
}
